import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
  PrimaryButtonSmallV2,
  PrimaryButtonXSmallV2,
  SecondaryButtonSmallV2,
  TertiaryButtonXSmallV2,
} from "../Buttons/Buttons";
import { ThumbsDownIcon, ThumbsUpIcon } from "../Icons/Icons";
import type { Feedback, FeedbackRating } from "./types";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-top: 10px;
`;

const CommentArea = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const CommentInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_md")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-family: inherit;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  resize: vertical;
`;

const CommentActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  justify-content: flex-end;
`;

export function MessageFeedback({
  feedback,
  onChange,
}: {
  feedback: Feedback | null | undefined;
  onChange: (next: Feedback | null) => void;
}) {
  const { t } = useTranslation();
  const current = feedback ?? null;
  const [composing, setComposing] = useState<null | FeedbackRating>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (composing) commentRef.current?.focus();
  }, [composing]);

  const handleClick = (rating: FeedbackRating) => {
    // Toggle: clicking the active thumb clears feedback.
    if (current?.rating === rating) {
      onChange(null);
      setComposing(null);
      setCommentDraft("");
      return;
    }
    if (rating === "down") {
      // Open the comment composer prefilled with any existing comment so the
      // user can edit instead of retyping.
      setCommentDraft(current?.comment ?? "");
      setComposing("down");
      onChange({ rating: "down", comment: current?.comment ?? null });
      return;
    }
    onChange({ rating: "up", comment: null });
    setComposing(null);
  };

  const submitComment = () => {
    const trimmed = commentDraft.trim();
    onChange({ rating: "down", comment: trimmed.length > 0 ? trimmed : null });
    setComposing(null);
  };

  // Toggle visual via Primary (active) ↔ Tertiary (inactive). Both V2.
  const upActive = current?.rating === "up";
  const downActive = current?.rating === "down";
  const ThumbsUpButton = upActive
    ? PrimaryButtonXSmallV2
    : TertiaryButtonXSmallV2;
  const ThumbsDownButton = downActive
    ? PrimaryButtonXSmallV2
    : TertiaryButtonXSmallV2;

  return (
    <>
      <Row>
        <ThumbsUpButton
          type="button"
          aria-pressed={upActive}
          aria-label={t("Helpful")}
          title={t("Helpful")}
          onClick={() => handleClick("up")}
        >
          <ThumbsUpIcon width={12} height={12} />
        </ThumbsUpButton>
        <ThumbsDownButton
          type="button"
          aria-pressed={downActive}
          aria-label={t("Not helpful")}
          title={t("Not helpful")}
          onClick={() => handleClick("down")}
        >
          <ThumbsDownIcon width={12} height={12} />
        </ThumbsDownButton>
      </Row>
      {composing === "down" && (
        <CommentArea>
          <CommentInput
            ref={commentRef}
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            placeholder={t("What went wrong? (optional)")}
            maxLength={2000}
          />
          <CommentActions>
            <SecondaryButtonSmallV2
              type="button"
              onClick={() => setComposing(null)}
            >
              {t("Skip")}
            </SecondaryButtonSmallV2>
            <PrimaryButtonSmallV2 type="button" onClick={submitComment}>
              {t("Submit")}
            </PrimaryButtonSmallV2>
          </CommentActions>
        </CommentArea>
      )}
    </>
  );
}
